import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { fmt } from "./finance-utils.js";
import { useMonth } from "./finance-month.js";
import { useFinanceCanEdit } from "./finance-access.js";
import { useT } from "../../i18n/index.js";

export function KpisPage() {
  const { data, isLoading } = trpc.finance.dashboard.summary.useQuery();
  const { data: monthly = [] } = trpc.finance.dashboard.monthly.useQuery();
  const { month, setMonth, months } = useMonth();
  const [showEmpresaDetail, setShowEmpresaDetail] = useState(false);
  const [periodo, setPeriodo] = useState("tudo");
  const t = useT();

  const currency = data?.currency === "BRL" ? "R$" : data?.currency === "USD" ? "$" : "€";
  const f = (v: number) => fmt(v, currency);

  if (isLoading)
    return <div style={{ color: "var(--text3)", fontSize: 13 }}>{t("financeKpis.loading")}</div>;
  if (!data) return null;

  const {
    balances,
    saldoNeto,
    saldoLandione,
    saldoPrevNeto,
    saldoPrevLandione,
    saldoEmpresaConsolidado,
    despesasConsolidadas,
    despesasPrevistas,
    saldoPrevisto,
    byCompany,
    fixedCosts,
    pctNeto,
    pctLandione,
  } = data;

  const despesasNeg = (balances?.["Despesas"] ?? 0) < 0;

  const curMonth = new Date().toISOString().slice(0, 7);
  const periodRows =
    periodo === "tudo"
      ? monthly
      : periodo === "atual"
        ? monthly.filter((m) => m.month === curMonth)
        : monthly.slice(-Number(periodo));
  const perIn = periodRows.reduce((s, m) => s + m.entradas, 0);
  const perOut = periodRows.reduce((s, m) => s + m.saidas, 0);

  // Saldos no fechamento do mês selecionado (último mês com dados ≤ selecionado); fallback all-time.
  const monthLabel = (m: string) =>
    [
      t("financeKpis.monthJan"),
      t("financeKpis.monthFeb"),
      t("financeKpis.monthMar"),
      t("financeKpis.monthApr"),
      t("financeKpis.monthMay"),
      t("financeKpis.monthJun"),
      t("financeKpis.monthJul"),
      t("financeKpis.monthAug"),
      t("financeKpis.monthSep"),
      t("financeKpis.monthOct"),
      t("financeKpis.monthNov"),
      t("financeKpis.monthDec"),
    ][Number(m.split("-")[1]) - 1] +
    "/" +
    m.slice(2, 4);
  const mb: Record<string, number> =
    ([...monthly].filter((m) => m.month <= month).pop()?.contas as any) ?? balances;
  const sNeto = mb["Neto"] ?? saldoNeto;
  const sLand = mb["Landione"] ?? saldoLandione;
  const sEmp = mb["Despesas"] ?? 0;

  const DashCard = ({
    label,
    value,
    sub,
    color,
  }: {
    label: string;
    value: number;
    sub?: string;
    color: string;
  }) => (
    <div className="dash-card">
      <div className="dash-card-label">{label}</div>
      <div className={`dash-card-val ${color}`}>{f(value)}</div>
      {sub && <div className="dash-card-sub">{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Seletor de mês — saldos no fechamento do mês */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "var(--text3)",
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}
        >
          {t("financeKpis.balancesInMonth")}
        </span>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{
            background: "var(--bg3)",
            border: "1px solid var(--border2)",
            color: "var(--text)",
            fontFamily: "var(--sans)",
            fontSize: 13,
            padding: "7px 10px",
            borderRadius: "var(--r)",
            cursor: "pointer",
          }}
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>
          {t("financeKpis.closingOf")} {monthLabel(month)}
        </span>
      </div>

      {despesasNeg && (
        <div
          className="alert"
          style={{
            marginBottom: 16,
            borderColor: "rgba(255,100,100,.4)",
            background: "rgba(255,100,100,.1)",
            color: "var(--red)",
          }}
        >
          {t("financeKpis.negativeCashAlertPre")} {f(balances["Despesas"] ?? 0)}{" "}
          {t("financeKpis.negativeCashAlertSuf")}
        </div>
      )}

      {/* Movimento do período */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {t("financeKpis.sectionPeriodMovement")}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { v: "atual", l: t("financeKpis.periodCurrentMonth") },
            { v: "3", l: t("financeKpis.period3m") },
            { v: "6", l: t("financeKpis.period6m") },
            { v: "12", l: t("financeKpis.period12m") },
            { v: "tudo", l: t("financeKpis.periodAll") },
          ].map((o) => (
            <button
              key={o.v}
              className={`btn${periodo === o.v ? " primary" : ""}`}
              style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={() => setPeriodo(o.v)}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 14,
          marginBottom: 24,
        }}
      >
        <DashCard
          label={t("financeKpis.cardInflows")}
          value={perIn}
          sub={t("financeKpis.subReceivedInPeriod")}
          color="green"
        />
        <DashCard
          label={t("financeKpis.cardOutflows")}
          value={perOut}
          sub={t("financeKpis.subSpentInPeriod")}
          color="red"
        />
        <DashCard
          label={t("financeKpis.cardResult")}
          value={perIn - perOut}
          sub={t("financeKpis.subInflowsMinusOutflows")}
          color={perIn - perOut >= 0 ? "green" : "red"}
        />
      </div>

      {/* Sócios */}
      <div className="section-title">{t("financeKpis.sectionPartners")}</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))",
          gap: 14,
          marginBottom: 24,
        }}
      >
        <DashCard
          label={t("financeKpis.cardBalanceNeto")}
          value={sNeto}
          sub={`${t("financeKpis.subShare")} ${pctNeto}%`}
          color={sNeto >= 0 ? "green" : "red"}
        />
        <DashCard
          label={t("financeKpis.cardBalanceLandione")}
          value={sLand}
          sub={`${t("financeKpis.subShare")} ${pctLandione}%`}
          color={sLand >= 0 ? "green" : "red"}
        />
        <DashCard
          label={t("financeKpis.cardForecastBalanceNeto")}
          value={saldoPrevNeto}
          sub={t("financeKpis.subAfterForecastExpenses")}
          color={saldoPrevNeto >= 0 ? "blue" : "red"}
        />
        <DashCard
          label={t("financeKpis.cardForecastBalanceLandione")}
          value={saldoPrevLandione}
          sub={t("financeKpis.subAfterForecastExpenses")}
          color={saldoPrevLandione >= 0 ? "blue" : "red"}
        />
      </div>

      {/* Empresa */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {t("financeKpis.labelCompany")}
        </div>
        <button
          style={{
            background: "none",
            border: "1px solid var(--border2)",
            color: "var(--text2)",
            fontSize: 11,
            padding: "3px 10px",
            borderRadius: 4,
            cursor: "pointer",
          }}
          onClick={() => setShowEmpresaDetail((v) => !v)}
        >
          {showEmpresaDetail ? t("financeKpis.toggleHide") : t("financeKpis.toggleByCompany")}
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 14,
          marginBottom: 8,
        }}
      >
        <DashCard
          label={t("financeKpis.cardOperatingCash")}
          value={sEmp}
          sub={t("financeKpis.subExpensesAccount")}
          color={sEmp >= 0 ? "green" : "red"}
        />
        <DashCard
          label={t("financeKpis.cardExpensesPaid")}
          value={despesasConsolidadas}
          sub={t("financeKpis.subPaidToDate")}
          color="amber"
        />
        <DashCard
          label={t("financeKpis.cardExpensesForecast")}
          value={despesasPrevistas}
          sub={t("financeKpis.subFixedPlusFuture")}
          color="amber"
        />
        <DashCard
          label={t("financeKpis.cardForecastBalance")}
          value={saldoPrevisto}
          sub={t("financeKpis.subAfterForecastExpenses")}
          color={saldoPrevisto >= 0 ? "blue" : "red"}
        />
      </div>

      {showEmpresaDetail && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
            gap: 14,
            marginBottom: 20,
          }}
        >
          {(["BR Storms", "Euro Storm"] as const).map((e) => {
            const s = byCompany[e];
            if (!s) return null;
            const dc = s.operationalCost + s.companyExpense;
            const pct =
              s.revenue > 0 ? Math.max(0, Math.min(100, ((s.revenue - dc) / s.revenue) * 100)) : 0;
            return (
              <div key={e} className="dash-card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>{e}</div>
                  <div
                    className={`dash-card-val ${s.balance >= 0 ? "green" : "red"}`}
                    style={{ fontSize: 18 }}
                  >
                    {f(s.balance)}
                  </div>
                </div>
                <div
                  style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}
                >
                  <div>
                    <div style={{ color: "var(--text3)" }}>{t("financeKpis.labelRevenue")}</div>
                    <div style={{ fontFamily: "var(--mono)", color: "var(--green)" }}>
                      {f(s.revenue)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: "var(--text3)" }}>{t("financeKpis.labelExpenses")}</div>
                    <div style={{ fontFamily: "var(--mono)", color: "var(--amber)" }}>{f(dc)}</div>
                  </div>
                </div>
                <div className="bar-wrap">
                  <div
                    className="bar-fill"
                    style={{ width: `${pct.toFixed(1)}%`, background: "var(--green)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Custos fixos */}
      <FixedCostsSection currency={currency} />

      {/* Breakdown */}
      <div className="section-title">{t("financeKpis.sectionBreakdown")}</div>
      <BreakdownTable byCompany={byCompany} f={f} />
    </div>
  );
}

function FixedCostsSection({ currency }: { currency: string }) {
  const qc = useQueryClient();
  const canEdit = useFinanceCanEdit();
  const { data = [] } = trpc.finance.fixedCosts.list.useQuery();
  const del = trpc.finance.fixedCosts.delete.useMutation({
    onSuccess: () => qc.invalidateQueries(),
  });
  const [showForm, setShowForm] = useState(false);
  const t = useT();

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {t("financeKpis.fixedCostsTitle")}
        </div>
        {canEdit && (
          <button
            style={{
              background: "rgba(79,142,247,.1)",
              border: "1px solid rgba(79,142,247,.3)",
              color: "var(--accent)",
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 4,
              cursor: "pointer",
            }}
            onClick={() => setShowForm(true)}
          >
            {t("financeKpis.addBtn")}
          </button>
        )}
      </div>
      {data.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text3)", padding: "8px 0" }}>
          {t("financeKpis.fixedCostsEmpty")}
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {data.map((c) => (
            <div
              key={c.id}
              style={{
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r)",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "var(--text2)" }}>
                  {c.company} · {c.category}
                </div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{c.description}</div>
              </div>
              <div
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 15,
                  color: "var(--amber)",
                  marginLeft: "auto",
                }}
              >
                {fmt(Number(c.amount), currency)}
                {t("financeKpis.perMonthSuffix")}
              </div>
              {canEdit && (
                <button
                  onClick={() => del.mutate({ id: c.id })}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text3)",
                    cursor: "pointer",
                    fontSize: 16,
                    padding: "0 2px",
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {showForm && <FixedCostForm onClose={() => setShowForm(false)} />}
    </div>
  );
}

function FixedCostForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [desc, setDesc] = useState("");
  const [valor, setValor] = useState("");
  const [emp, setEmp] = useState("Euro Storm");
  const [cat, setCat] = useState("operational");
  const t = useT();

  const create = trpc.finance.fixedCosts.create.useMutation({
    onSuccess: () => {
      qc.invalidateQueries();
      onClose();
    },
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          {t("financeKpis.formTitle")}
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 2 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeKpis.labelDescription")}
            </div>
            <input
              className="input"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={t("financeKpis.placeholderDescription")}
              autoFocus
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeKpis.labelAmount")}
            </div>
            <input
              className="input"
              type="number"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="0.00"
              min={0}
              step={0.01}
              style={{ width: 100 }}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeKpis.labelCompany")}
            </div>
            <select className="input" value={emp} onChange={(e) => setEmp(e.target.value)}>
              <option value="Euro Storm">Euro Storm</option>
              <option value="BR Storms">BR Storms</option>
              <option value="Geral">{t("financeKpis.optionGeneral")}</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              {t("financeKpis.labelCategory")}
            </div>
            <select className="input" value={cat} onChange={(e) => setCat(e.target.value)}>
              <option value="operational">{t("financeKpis.optionOperational")}</option>
              <option value="tax">{t("financeKpis.optionTax")}</option>
              <option value="administrative">{t("financeKpis.optionAdministrative")}</option>
              <option value="other">{t("financeKpis.optionOther")}</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>
            {t("financeKpis.cancel")}
          </button>
          <button
            className="btn primary"
            disabled={create.isPending || !desc || !valor}
            onClick={() =>
              create.mutate({
                description: desc,
                amount: Number(valor),
                company: emp as any,
                category: cat as any,
              })
            }
          >
            {t("financeKpis.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function BreakdownTable({
  byCompany,
  f,
}: {
  byCompany: Record<string, any>;
  f: (v: number) => string;
}) {
  const t = useT();
  const br = byCompany["BR Storms"] ?? {};
  const eu = byCompany["Euro Storm"] ?? {};

  const rows = [
    {
      label: t("financeKpis.rowGrossRevenue"),
      brV: br.revenue ?? 0,
      euV: eu.revenue ?? 0,
      bold: false,
    },
    {
      label: t("financeKpis.rowOperationalCosts"),
      brV: -(br.operationalCost ?? 0),
      euV: -(eu.operationalCost ?? 0),
      bold: false,
    },
    {
      label: t("financeKpis.rowCompanyExpenses"),
      brV: -(br.companyExpense ?? 0),
      euV: -(eu.companyExpense ?? 0),
      bold: false,
    },
    {
      label: t("financeKpis.rowCompanyAccountBalance"),
      brV: br.balance ?? 0,
      euV: eu.balance ?? 0,
      bold: true,
    },
  ];

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: "var(--r2)",
        overflow: "hidden",
        marginBottom: 24,
      }}
    >
      <thead>
        <tr>
          {[
            t("financeKpis.labelCategory"),
            "BR Storms",
            "Euro Storm",
            t("financeKpis.colTotal"),
          ].map((h) => (
            <th
              key={h}
              style={{
                background: "var(--bg3)",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "var(--text3)",
                padding: "10px 16px",
                textAlign: "left",
                borderBottom: "1px solid var(--border)",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const tot = r.brV + r.euV;
          const c = (v: number) => (v >= 0 ? "var(--green)" : "var(--red)");
          return (
            <tr key={r.label} style={r.bold ? { background: "var(--bg3)" } : {}}>
              <td
                style={{
                  padding: "10px 16px",
                  fontSize: 13,
                  borderBottom: "1px solid var(--border)",
                  fontWeight: r.bold ? 600 : 400,
                }}
              >
                {r.label}
              </td>
              {[r.brV, r.euV, tot].map((v, i) => (
                <td
                  key={i}
                  style={{
                    padding: "10px 16px",
                    fontSize: 13,
                    borderBottom: "1px solid var(--border)",
                    fontFamily: "var(--mono)",
                    color: c(v),
                    fontWeight: i === 2 ? 600 : 400,
                  }}
                >
                  {f(v)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Missing import
import { useQueryClient } from "@tanstack/react-query";
